import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { LayoutItem } from './types';
import { WIDGET_REGISTRY } from './registry';

// Masonry-parametre — auto-rows er små units og widgets får row-span baseret
// på deres faktiske renderede højde. Row-gap er 0; den visuelle vertikale
// afstand bygges ind i span'et (+VISUAL_GAP) saa horizontal og vertikal
// afstand altid er ens uafhaengigt af widget-hojder.
const ROW_UNIT   = 4;   // px pr. grid-row (mindre = mere praecis, max 3px overspil)
const VISUAL_GAP = 24;  // matcher column-gap (gap-x-6 = 1.5rem)

function computeSpan(heightPx: number): number {
  if (heightPx <= 0) return 50;
  return Math.max(1, Math.ceil((heightPx + VISUAL_GAP) / ROW_UNIT));
}

// ── Drag-handle icon ───────────────────────────────────────────────────────────

function GripIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
      <circle cx="8"  cy="5"  r="1.5" />
      <circle cx="16" cy="5"  r="1.5" />
      <circle cx="8"  cy="12" r="1.5" />
      <circle cx="16" cy="12" r="1.5" />
      <circle cx="8"  cy="19" r="1.5" />
      <circle cx="16" cy="19" r="1.5" />
    </svg>
  );
}

// ── Sortable wrapper per widget ────────────────────────────────────────────────

interface SortableWidgetProps {
  id: string;
  colSpan: number;
  children: React.ReactNode;
}

function SortableWidget({ id, colSpan, children }: SortableWidgetProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const innerRef = useRef<HTMLDivElement>(null);
  const [rowSpan, setRowSpan] = useState(20);

  // Initial mål før paint så vi undgår flicker
  useLayoutEffect(() => {
    if (!innerRef.current) return;
    setRowSpan(computeSpan(innerRef.current.getBoundingClientRect().height));
  }, []);

  // ResizeObserver til løbende opdatering når data loader / vinduet ændrer størrelse
  useEffect(() => {
    if (!innerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const next = computeSpan(entry.contentRect.height);
        setRowSpan(prev => prev !== next ? next : prev);
      }
    });
    observer.observe(innerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 50 : undefined,
      }}
    >
      <div ref={innerRef} className="relative group">
        {/* Drag handle — vises ved hover */}
        <button
          {...attributes}
          {...listeners}
          className="absolute top-3 right-3 z-10 p-1 rounded text-gray-600 opacity-0 group-hover:opacity-100 hover:text-gray-400 transition-opacity cursor-grab active:cursor-grabbing"
          aria-label="Flyt widget"
          tabIndex={-1}
        >
          <GripIcon />
        </button>
        {children}
      </div>
    </div>
  );
}

// ── DashboardGrid ──────────────────────────────────────────────────────────────

interface Props {
  items: LayoutItem[];
  onReorder: (items: LayoutItem[]) => void;
}

export default function DashboardGrid({ items, onReorder }: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const visible = items.filter(i => i.visible_web);
  const ids = visible.map(i => i.widget_key);

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIdx = ids.indexOf(active.id as string);
    const newIdx = ids.indexOf(over.id as string);
    const reordered = arrayMove(visible, oldIdx, newIdx).map((item, idx) => ({
      ...item,
      position: idx,
    }));

    // Bevar ikke-synlige widgets i listen, men opdater positioner for synlige
    const hiddenItems = items.filter(i => !i.visible_web);
    onReorder([...reordered, ...hiddenItems]);
  }

  if (!visible.length) {
    return (
      <p className="text-sm text-ink-500 mt-8 text-center">
        Ingen widgets er aktiveret — klik "Tilpas widgets" for at vælge.
      </p>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div
          className="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-0"
          style={{
            gridAutoRows: `${ROW_UNIT}px`,
            gridAutoFlow: 'row dense',
          }}
        >
          {visible.map(item => {
            const def = WIDGET_REGISTRY.find(d => d.key === item.widget_key);
            if (!def) return null;
            const Component = def.component;
            return (
              <SortableWidget
                key={item.widget_key}
                id={item.widget_key}
                colSpan={item.col_span}
              >
                <Component />
              </SortableWidget>
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
}
